import { useRoute } from '#imports'
import { type ComponentPublicInstance, onMounted, onUnmounted } from 'vue'

import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import type { EventData } from '@backmarket/nuxt-module-tracking/types'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { capitalize } from '@backmarket/utils/string/capitalize'
import { cleanAlgoliaLabel } from '@backmarket/utils/string/cleanAlgoliaLabel'
import { useIntersectionObserver, useTemplateRefsList } from '@vueuse/core'

import type { Product } from '../models/product'

export type Provider = 'algolia' | 'product page' | 'unknown'

export interface ProductTrackingData extends Partial<EventData> {
  averageRate?: number
  brand: string
  category: string
  color?: string
  currency: string
  dealType: string
  image: string
  isOutOfStock: boolean
  isSwapEnabled: boolean
  list?: string
  listingId: string
  merchantId?: string
  mobilePlanOfferSelected?: string
  model: string
  name: string
  numberTotalReviews?: number
  position?: number
  price: string
  productComponent?: string
  productRecoComponentPosition?: number
  provider?: string
  shippingDelay?: string
  uuid: string
  variant: number
  warrantyDuration?: number
  isFlashSales: boolean
  productGreatPrice: boolean
  productPriceDrop: boolean
  productMarketingTag: string
  personalizationContentCardFamily?: string
}

function cleanEventData(data: Partial<EventData>): EventData {
  const result: EventData = {}

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      result[key] = value
    }
  }

  return result
}

export function productToTrackingData(
  product: Product,
  extraTrackingData: ExtraTrackingData,
  dealType: 'normal' = 'normal',
): ProductTrackingData {
  const granularity = product.granularity || extraTrackingData.granularity

  return {
    averageRate: product.reviewRating?.average,
    hasReviews: !!product.reviewRating?.count,
    brand: product.brandClean ?? product.brand,
    category: product.category,
    color: product.specifications?.color?.[0]?.name,
    currency: product.price?.currency,
    dealType,
    id: product.productId,
    isOutOfStock: product.availableStock === 0,
    isSwapEnabled: false,
    image: product.image,
    list: extraTrackingData.list,
    listingId: product.listingId,
    merchantId: product.sellerId,
    mobilePlanOfferSelected: product.mobilePlanOfferId ?? undefined,
    model: product.model,
    name: product.title,
    numberTotalReviews: product.reviewRating?.count,
    position: extraTrackingData.position,
    price: product.price?.amount,
    productComponent: extraTrackingData.productComponent,
    productRecoComponentPosition:
      extraTrackingData.productRecoComponentPosition,
    provider: extraTrackingData.provider,
    shippingDelay: '',
    uuid: product.id,
    variant: product.grade?.value,
    warrantyDuration: product.warrantyDuration,
    isFlashSales: product.deals?.some((d) => d.type === 'flash_sales') ?? false,
    productGreatPrice:
      granularity === 'backbox' &&
      (product.deals?.some((d) => d.type === 'competitive_price') ?? false),
    productPriceDrop:
      granularity === 'backbox' &&
      (product.deals?.some((d) => d.type === 'seller_flash_sales') ?? false),
    productMarketingTag: product.marketingTags?.[0]?.code ?? 'no_tag',
    source: extraTrackingData.source,
    personalizationContentCardFamily:
      extraTrackingData.personalizationContentCardFamily,
  }
}

function usePageReference() {
  const { name, params, fullPath } = useRoute()
  const pageNameParam = params.pageName
  const pageName = Array.isArray(pageNameParam)
    ? pageNameParam.join(',')
    : pageNameParam

  // Convert name to string to avoid type errors
  let routeName = ''
  if (typeof name === 'string') {
    routeName = name
  } else if (typeof name === 'symbol') {
    routeName = String(name)
  }

  return pageName ?? routeName ?? fullPath
}

export function useListTrackingData({
  blockPosition,
}: {
  blockPosition?: number
}) {
  const pageReference = usePageReference()

  return `${pageReference} ${blockPosition}`
}

export function useImpressionTracking(ms = 1000) {
  const trackingData = new Map<Element, ProductTrackingData>()
  const bucket: Set<ProductTrackingData> = new Set()
  const targets = useTemplateRefsList<HTMLElement>()
  let impressionTrackingInterval: ReturnType<typeof setInterval> | null = null

  const { stop } = useIntersectionObserver(
    targets,
    (entries) => {
      for (const entry of entries) {
        const trackingDataItem = trackingData.get(entry.target)
        if (entry.isIntersecting && trackingDataItem) {
          bucket.add(trackingDataItem)
        }
      }
    },
    { threshold: 0.8 },
  )

  /**
   * Use is in the template as follow
   * :ref="setupViewportTracking(data)"
   *
   * @param data a serializable JSON object
   */
  function setupViewportTracking(data: ProductTrackingData) {
    return function setTrackingRef(
      el: Element | ComponentPublicInstance | null,
    ) {
      if (el && el instanceof HTMLElement) {
        trackingData.set(el, data)
        targets.value.push(el)
      }

      return el
    }
  }

  function setupViewportTrackingProduct(
    data: Product,
    extra: ExtraTrackingData,
  ) {
    return setupViewportTracking(productToTrackingData(data, extra))
  }

  const { trackProductImpressionBatched, trackProductImpression } =
    useTracking()

  onMounted(() => {
    impressionTrackingInterval = setInterval(() => {
      if (bucket.size > 0) {
        if (bucket.size === 1) {
          trackProductImpression(cleanEventData(Array.from(bucket)[0]))
        } else {
          trackProductImpressionBatched(
            Array.from(bucket).map((eventData) => cleanEventData(eventData)),
          )
        }
        bucket.clear()
      }
    }, ms)
  })

  onUnmounted(() => {
    if (impressionTrackingInterval) {
      clearInterval(impressionTrackingInterval)
    }
    stop()
    trackingData.clear()
  })

  return { setupViewportTracking, setupViewportTrackingProduct }
}

export type ExtraTrackingData = Partial<{
  provider: Provider
  list: string
  position?: number
  source?: string
  queryID?: string
  index?: string
  granularity?: 'parent' | 'variant' | 'backbox'
  productComponent: string
  productRecoComponentPosition: number
  personalizationContentCardFamily?: string
}>
export function useClickTracking() {
  const { trackProductClick: genericTrackProductClick, trackSearchAnalytics } =
    useTracking()
  const visitorId = useVisitorId()

  function trackProductClick(
    product: Product,
    extraTrackingData: ExtraTrackingData,
  ) {
    genericTrackProductClick(
      cleanEventData(productToTrackingData(product, extraTrackingData)),
    )

    const { queryID, index, position } = extraTrackingData
    if (queryID && index && position && product.objectId) {
      trackSearchAnalytics({
        type: 'click',
        objectId: product.objectId,
        searchQueryID: queryID,
        tokenId: visitorId,
        position,
        index,
      })
    }
  }

  return { trackProductClick }
}

/* eslint-disable camelcase */
function toGa4AddToCartItem(data: ProductTrackingData, locationId: string) {
  const price =
    typeof data.price === 'string' ? parseFloat(data.price) : data.price

  return {
    item_id:
      typeof data.id === 'string' || typeof data.id === 'number'
        ? String(data.id)
        : '',
    item_uuid: typeof data.uuid === 'string' ? data.uuid : '',
    item_name: data.name,
    item_brand: capitalize(cleanAlgoliaLabel(data.brand)),
    item_category: data.category,
    price,
    quantity: 1,
    affiliation: data.merchantId ?? '',
    index: data.position,
    item_list_id: data.list,
    item_list_name: data.list,
    item_variant: data.variant,
    item_warranty_duration: data.warrantyDuration,
    location_id: locationId,
    google_business_vertical: 'retail',
  }
}
/* eslint-enable camelcase */

export function useAddToCartTracking() {
  const { countryCode } = useMarketplace().market
  const { hasBuybackOffer } = useBuybackOffer()

  return {
    trackAddToCart: (trackingData: ProductTrackingData, zone: string) => {
      const { trackAddToCart: _trackAddToCart, trackAddToCartGa4 } =
        useTracking()
      _trackAddToCart({ ...trackingData, zone })
      const ga4Item = toGa4AddToCartItem(trackingData, countryCode)
      trackAddToCartGa4({
        zone,
        currency: trackingData.currency,
        hasTradeIn: hasBuybackOffer.value,
        value: (ga4Item.price ?? 0) * ga4Item.quantity,
        items: [{ ...ga4Item, item_list_id: zone, item_list_name: zone }],
      })
    },

    trackAddToCartBatch: (
      products: Product[],
      extraTrackingData: ExtraTrackingData,
      list: string,
    ) => {
      const { trackAddToCartBatch, trackAddToCartGa4 } = useTracking()
      const trackingProducts = products.map((product) =>
        productToTrackingData(product, extraTrackingData),
      )
      trackAddToCartBatch({ products: trackingProducts, list })
      const ga4Items = trackingProducts.map((p) => ({
        ...toGa4AddToCartItem(p, countryCode),
        /* eslint-disable camelcase */
        item_list_id: list,
        item_list_name: list,
        /* eslint-enable camelcase */
      }))
      const currency = trackingProducts[0]?.currency ?? ''
      const value = ga4Items.reduce(
        (sum, item) => sum + (item.price ?? 0) * item.quantity,
        0,
      )
      trackAddToCartGa4({
        zone: list,
        currency,
        hasTradeIn: hasBuybackOffer.value,
        value,
        items: ga4Items,
      })
    },
  }
}
